import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingTiersSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedBilling, setSelectedBilling] = useState('annual');

  const translations = {
    en: {
      title: "Choose Your Perfect ERP Plan",
      subtitle: "Designed for different scales of aluminum extrusion operations",
      billingToggle: {
        monthly: "Monthly",
        annual: "Annual",
        save: "Save 20%"
      },
      plans: {
        starter: {
          name: "Starter",
          subtitle: "Perfect for small operations",
          monthlyPrice: "₹12,000",
          annualPrice: "₹9,600",
          originalPrice: "₹12,000",
          users: "Up to 5 users",
          storage: "50GB storage",
          features: [
            "Basic inventory management",
            "Order processing",
            "Customer management",
            "Basic reporting",
            "Email support",
            "Mobile app access"
          ],
          cta: "Start Free Trial"
        },
        professional: {
          name: "Professional",
          subtitle: "Best for growing businesses",
          monthlyPrice: "₹25,000",
          annualPrice: "₹20,000",
          originalPrice: "₹25,000",
          users: "Up to 25 users",
          storage: "200GB storage",
          features: [
            "Advanced inventory management",
            "Production planning",
            "Quality control",
            "Financial management",
            "Advanced analytics",
            "Phone & email support",
            "API integrations",
            "Custom reports"
          ],
          cta: "Start Free Trial",
          popular: true
        },
        enterprise: {
          name: "Enterprise",
          subtitle: "For large-scale operations",
          monthlyPrice: "₹50,000",
          annualPrice: "₹40,000",
          originalPrice: "₹50,000",
          users: "Unlimited users",
          storage: "1TB storage",
          features: [
            "Complete ERP suite",
            "Multi-location support",
            "Advanced workflow automation",
            "Custom integrations",
            "Dedicated support manager",
            "24/7 priority support",
            "Training & onboarding",
            "Custom development"
          ],
          cta: "Contact Sales"
        }
      },
      comparison: {
        title: "Feature Comparison",
        features: [
          "Inventory Management",
          "Order Processing",
          "Production Planning",
          "Quality Control",
          "Financial Management",
          "Multi-location Support",
          "API Integrations",
          "Custom Reports"
        ]
      }
    },
    hi: {
      title: "अपना सही ERP प्लान चुनें",
      subtitle: "एल्यूमीनियम एक्सट्रूज़न संचालन के विभिन्न स्केल के लिए डिज़ाइन किया गया",
      billingToggle: {
        monthly: "मासिक",
        annual: "वार्षिक",
        save: "20% बचाएं"
      },
      plans: {
        starter: {
          name: "स्टार्टर",
          subtitle: "छोटे संचालन के लिए सही",
          monthlyPrice: "₹12,000",
          annualPrice: "₹9,600",
          originalPrice: "₹12,000",
          users: "5 उपयोगकर्ता तक",
          storage: "50GB स्टोरेज",
          features: [
            "बेसिक इन्वेंटरी प्रबंधन",
            "ऑर्डर प्रोसेसिंग",
            "ग्राहक प्रबंधन",
            "बेसिक रिपोर्टिंग",
            "ईमेल सहायता",
            "मोबाइल ऐप एक्सेस"
          ],
          cta: "मुफ्त ट्रायल शुरू करें"
        },
        professional: {
          name: "प्रोफेशनल",
          subtitle: "बढ़ते व्यवसाय के लिए सबसे अच्छा",
          monthlyPrice: "₹25,000",
          annualPrice: "₹20,000",
          originalPrice: "₹25,000",
          users: "25 उपयोगकर्ता तक",
          storage: "200GB स्टोरेज",
          features: [
            "एडवांस इन्वेंटरी प्रबंधन",
            "उत्पादन योजना",
            "गुणवत्ता नियंत्रण",
            "वित्तीय प्रबंधन",
            "एडवांस एनालिटिक्स",
            "फोन और ईमेल सहायता",
            "API इंटीग्रेशन",
            "कस्टम रिपोर्ट्स"
          ],
          cta: "मुफ्त ट्रायल शुरू करें",
          popular: true
        },
        enterprise: {
          name: "एंटरप्राइज़",
          subtitle: "बड़े पैमाने के संचालन के लिए",
          monthlyPrice: "₹50,000",
          annualPrice: "₹40,000",
          originalPrice: "₹50,000",
          users: "असीमित उपयोगकर्ता",
          storage: "1TB स्टोरेज",
          features: [
            "संपूर्ण ERP सूट",
            "मल्टी-लोकेशन सपोर्ट",
            "एडवांस वर्कफ़्लो ऑटोमेशन",
            "कस्टम इंटीग्रेशन",
            "समर्पित सहायता प्रबंधक",
            "24/7 प्राथमिकता सहायता",
            "प्रशिक्षण और ऑनबोर्डिंग",
            "कस्टम डेवलपमेंट"
          ],
          cta: "सेल्स से संपर्क करें"
        }
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const formatPrice = (plan) => {
    if (selectedBilling === 'annual') {
      return {
        price: plan.annualPrice,
        period: currentLanguage === 'en' ? '/month' : '/माह',
        note: currentLanguage === 'en' ? 'billed annually' : 'वार्षिक बिलिंग'
      };
    } else {
      return {
        price: plan.monthlyPrice,
        period: currentLanguage === 'en' ? '/month' : '/माह',
        note: currentLanguage === 'en' ? 'billed monthly' : 'मासिक बिलिंग'
      };
    }
  };

  return (
    <section id="pricing-tiers" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-8">
            {t.subtitle}
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-surface-100 rounded-lg p-1">
            <button
              onClick={() => setSelectedBilling('monthly')}
              className={`px-6 py-3 rounded-lg transition-all duration-300 ${
                selectedBilling === 'monthly' ?'bg-white text-primary shadow-sm' :'text-text-secondary hover:text-primary'
              }`}
            >
              {t.billingToggle.monthly}
            </button>
            <button
              onClick={() => setSelectedBilling('annual')}
              className={`px-6 py-3 rounded-lg transition-all duration-300 relative ${
                selectedBilling === 'annual' ?'bg-white text-primary shadow-sm' :'text-text-secondary hover:text-primary'
              }`}
            >
              {t.billingToggle.annual}
              <span className="absolute -top-2 -right-2 bg-accent text-white text-xs px-2 py-1 rounded-full">
                {t.billingToggle.save}
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Starter Plan */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border hover:shadow-xl transition-shadow duration-300">
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-primary mb-2">
                {t.plans.starter.name}
              </h3>
              <p className="text-text-secondary mb-6">
                {t.plans.starter.subtitle}
              </p>
              
              <div className="mb-4">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {formatPrice(t.plans.starter).price}
                  <span className="text-lg text-text-secondary">
                    {formatPrice(t.plans.starter).period}
                  </span>
                </div>
                <div className="text-sm text-text-secondary">
                  {formatPrice(t.plans.starter).note}
                </div>
              </div>

              <div className="space-y-1 text-sm text-text-secondary">
                <div>{t.plans.starter.users}</div>
                <div>{t.plans.starter.storage}</div>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {t.plans.starter.features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <Icon name="Check" size={12} color="var(--color-primary)" />
                  </div>
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              className="w-full border-primary text-primary hover:bg-primary hover:text-white"
            >
              {t.plans.starter.cta}
            </Button>
          </div>

          {/* Professional Plan */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-primary hover:shadow-xl transition-shadow duration-300 relative">
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-primary text-white px-4 py-2 rounded-full text-sm font-medium">
                {currentLanguage === 'en' ? 'Most Popular' : 'सबसे लोकप्रिय'}
              </div>
            </div>

            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-primary mb-2">
                {t.plans.professional.name}
              </h3>
              <p className="text-text-secondary mb-6">
                {t.plans.professional.subtitle}
              </p>
              
              <div className="mb-4">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {formatPrice(t.plans.professional).price}
                  <span className="text-lg text-text-secondary">
                    {formatPrice(t.plans.professional).period}
                  </span>
                </div>
                <div className="text-sm text-text-secondary">
                  {formatPrice(t.plans.professional).note}
                </div>
              </div>

              <div className="space-y-1 text-sm text-text-secondary">
                <div>{t.plans.professional.users}</div>
                <div>{t.plans.professional.storage}</div>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {t.plans.professional.features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <Icon name="Check" size={12} color="var(--color-primary)" />
                  </div>
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              variant="primary"
              className="w-full bg-gradient-accent hover:bg-accent-600 shadow-cta"
            >
              {t.plans.professional.cta}
            </Button>
          </div>

          {/* Enterprise Plan */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border hover:shadow-xl transition-shadow duration-300">
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-primary mb-2">
                {t.plans.enterprise.name}
              </h3>
              <p className="text-text-secondary mb-6">
                {t.plans.enterprise.subtitle}
              </p>
              
              <div className="mb-4">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {formatPrice(t.plans.enterprise).price}
                  <span className="text-lg text-text-secondary">
                    {formatPrice(t.plans.enterprise).period}
                  </span>
                </div>
                <div className="text-sm text-text-secondary">
                  {formatPrice(t.plans.enterprise).note}
                </div>
              </div>

              <div className="space-y-1 text-sm text-text-secondary">
                <div>{t.plans.enterprise.users}</div>
                <div>{t.plans.enterprise.storage}</div>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {t.plans.enterprise.features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <Icon name="Check" size={12} color="var(--color-primary)" />
                  </div>
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              className="w-full border-primary text-primary hover:bg-primary hover:text-white"
            >
              {t.plans.enterprise.cta}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingTiersSection;